import { type FC, Suspense, useState, useCallback, type ReactNode } from 'react';
import { useQueryLoader } from 'react-relay';
import { getBuyerId } from 'dibs-cookie-jar';
import { Spinner } from 'dibs-elements/exports/Spinner';

import { useNftItemTileContext } from '../NftItemTileContext';
import { NftSecondaryMarketModalLazy } from './NftSecondaryMarketModal/NftSecondaryMarketModalLazy';
import { nftSecondaryMarketModalQuery } from './NftSecondaryMarketModal/nftSecondaryMarketModalQuery';
import { NftSecondaryMarketAuctionCancelledToast } from './NftSecondaryMarketToasts/NftSecondaryMarketAuctionCancelledToast';
import { NftSecondaryMarketLoadingToast } from './NftSecondaryMarketToasts/NftSecondaryMarketLoadingToast';
import { type nftSecondaryMarketModalQuery as QueryType } from './NftSecondaryMarketModal/__generated__/nftSecondaryMarketModalQuery.graphql';

import { type NftSecondaryMarketModalProps } from '../components/NftSecondaryMarketModal/NftSecondaryMarketModal';

import styles from './styles/NftItemTileSecondaryMarketButton.scss';

const isClient = typeof window !== 'undefined';

type SharedProps = Pick<NftSecondaryMarketModalProps, 'onClose' | 'isOpen'> & {
    queryRef?: NftSecondaryMarketModalProps['queryRef'] | null;
};
type NftItemId = string | null;
type UseModalHandlersProps = { onOpen: () => void } & SharedProps;
type ModalProps = {
    children?: ReactNode;
    isMobile: boolean;
    hideFallback?: boolean;
} & SharedProps;

export const useModalHandlers = (nftItemId: NftItemId): UseModalHandlersProps => {
    const { secondaryMarketRefetch, displayToastNotification } = useNftItemTileContext();
    const [isOpen, setIsOpen] = useState(false);
    const [queryRef, loadQuery] = useQueryLoader<QueryType>(nftSecondaryMarketModalQuery, null);
    const onOpen = useCallback(() => {
        if (nftItemId) {
            loadQuery(
                { nftItemId, userId: getBuyerId(document.cookie), fetchUser: true },
                { fetchPolicy: 'network-only' }
            );
            setIsOpen(true);
        }
    }, [loadQuery, nftItemId]);

    const onClose: SharedProps['onClose'] = useCallback(
        async (unfinishedTransaction, shouldRefetch) => {
            setIsOpen(false);

            if (shouldRefetch) {
                // if modal closed before tx finished, show toast and wait for tx to be processed
                if (unfinishedTransaction) {
                    displayToastNotification?.(<NftSecondaryMarketLoadingToast />);
                    await unfinishedTransaction;
                }

                secondaryMarketRefetch?.();

                // show success toaster
                displayToastNotification?.(<NftSecondaryMarketAuctionCancelledToast />, true);
            }
        },
        [displayToastNotification, secondaryMarketRefetch]
    );
    return { isOpen, onOpen, onClose, queryRef };
};

export const Modal: FC<ModalProps> = ({ queryRef, ...props }) =>
    isClient && queryRef ? (
        <Suspense
            fallback={
                !props?.hideFallback ? (
                    <Spinner size="tiny" black containerClass={styles.spinnerContainer} />
                ) : null
            }
        >
            <NftSecondaryMarketModalLazy queryRef={queryRef} {...props} />
        </Suspense>
    ) : null;
